import React from 'react';
import { ShieldAlert } from 'lucide-react';
import { SidebarMenuItem, SidebarMenuButton } from 'src/shared/components/ui/sidebar';
import { useAppSelector } from 'src/store';
import { getAppRoutesAvailability, getPortfolioRoutesAvailability } from 'src/routes/state/routesSelector';
import { Link, useLocation } from 'react-router-dom';
import useAppNavigation from 'src/shared/hooks/useAppNavigation';
import { ProductAccess } from 'src/shared/types/productLicensing';
import { useIsSetToHidden } from 'src/shared/hooks/useProductLicensing';
import { ProductKey } from 'src/shared/types/engine/products';
import NestedSidebarMenuItem, { HiddenFromCustomersMark } from './NestedSidebarMenuItem';

type GlobalNatureRiskMenuProps = {
    readonly access: ProductAccess;
};

const GlobalNatureRiskMenu = ({ access }: GlobalNatureRiskMenuProps) => {
    const appRoutesAvailability = useAppSelector(getAppRoutesAvailability);
    const portfolioRoutesAvailability = useAppSelector(getPortfolioRoutesAvailability);
    const navigate = useAppNavigation();
    const { pathname } = useLocation();
    // Only an internal user reaches the menu when it is set to Hidden; the mark tells them customers do not.
    const isHiddenFromCustomers = useIsSetToHidden(ProductKey.GLOBAL_NATURE_RISK);

    if (!appRoutesAvailability.portfolio || !portfolioRoutesAvailability.globalNatureRiskSiteList) {
        return null;
    }

    // Nothing while loading as well: a placeholder would show a hidden
    // product's name to the customer it is hidden from.
    if (access === ProductAccess.HIDDEN || access === ProductAccess.LOADING) {
        return null;
    }

    const isGlobalNatureRiskActive = pathname.includes('/portfolio/global-nature-risk');
    const subItems = [
        {
            title: 'Overview',
            testId: 'global-nature-risk-overview-menu-item',
            to: navigate.paths.globalNatureRiskOverview(),
            isActive: pathname.includes('/portfolio/global-nature-risk/overview'),
            isHiddenFromCustomers,
        },
        {
            title: 'State of Nature',
            testId: 'global-nature-risk-state-of-nature-menu-item',
            to: navigate.paths.globalNatureRiskStateOfNature(),
            isActive: pathname.includes('/portfolio/global-nature-risk/state-of-nature'),
            isHiddenFromCustomers,
        },
        {
            title: 'Impacts & Dependencies',
            testId: 'global-nature-risk-impacts-and-dependencies-menu-item',
            to: navigate.paths.globalNatureRiskImpactsAndDependencies(),
            isActive: pathname.includes('/portfolio/global-nature-risk/impacts-and-dependencies'),
            isHiddenFromCustomers,
        },
        {
            title: 'Site Explorer',
            testId: 'global-nature-risk-site-list-menu-item',
            to: navigate.paths.globalNatureRiskSiteList(),
            isActive: pathname.includes('/site-list'),
            isHiddenFromCustomers,
        },
    ];

    if (access === ProductAccess.DEFAULT) {
        return (
            <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip='Global Nature Risk' isActive={isGlobalNatureRiskActive}>
                    <Link to={navigate.paths.globalNatureRiskOverview()} data-testid='global-nature-risk-menu-button'>
                        <ShieldAlert />
                        <span>Global Nature Risk</span>
                        {isHiddenFromCustomers && <HiddenFromCustomersMark />}
                    </Link>
                </SidebarMenuButton>
            </SidebarMenuItem>
        );
    }

    return (
        <NestedSidebarMenuItem
            label='Global Nature Risk'
            testId='global-nature-risk-menu-button'
            icon={<ShieldAlert />}
            isActive={isGlobalNatureRiskActive}
            defaultOpen={isGlobalNatureRiskActive}
            subItems={subItems}
        />
    );
};

export default GlobalNatureRiskMenu;
